import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { CelestialBodyDefinition } from "../../types/celestialBody";
import type { VisualizationScale } from "../../types/astronomy";
import { toScenePosition, toSceneRadius } from "../../utils/scaling";
import { degToRad } from "../../astronomy/kepler";
import { PlanetaryRings } from "./PlanetaryRings";
import { BodyLabel } from "./BodyLabel";
import { AxialTiltIndicator } from "./AxialTiltIndicator";
import { VelocityVector } from "./VelocityVector";
import { useSceneStore } from "../../store/sceneStore";
import { getLiveSimulationDate } from "../../store/liveClock";
import { dateToJulianDate } from "../../astronomy/julianDate";
import { calculateBodyRuntimeState } from "../../astronomy/ephemeris";
import { catalogById } from "../../data/catalog";
import type { Vector3Data } from "../../types/astronomy";

export interface CelestialBodyProps {
  definition: CelestialBodyDefinition;
  scale: VisualizationScale;
  selected: boolean;
  onSelect: (bodyId: string) => void;
}

export function CelestialBodyMesh({
  definition,
  scale,
  selected,
  onSelect,
}: CelestialBodyProps) {
  const groupRef = useRef<THREE.Group>(null);
  const meshRef = useRef<THREE.Mesh>(null);
  const velocityRef = useRef<Vector3Data>({ x: 0, y: 0, z: 0 });
  const showLabels = useSceneStore((s) => s.showLabels);
  const showAxes = useSceneStore((s) => s.showAxes);
  const showVelocityVectors = useSceneStore((s) => s.showVelocityVectors);
  const focusedBodyId = useSceneStore((s) => s.focusedBodyId);
  const selectedBodyId = useSceneStore((s) => s.selectedBodyId);

  const radius = toSceneRadius(definition.physical.meanRadiusKm, scale);
  const segments = definition.type === "moon" ? 24 : 64;

  const showNameLabel =
    showLabels &&
    (definition.type !== "moon" ||
      selected ||
      selectedBodyId === definition.parentId ||
      focusedBodyId === definition.parentId ||
      focusedBodyId === definition.id ||
      definition.physical.meanRadiusKm >= 20);

  useFrame(() => {
    const jd = dateToJulianDate(getLiveSimulationDate());
    const state = calculateBodyRuntimeState(definition, jd, catalogById);
    velocityRef.current = state.velocityKmS;
    const position = toScenePosition(state.absolutePositionKm, scale);

    if (groupRef.current) {
      groupRef.current.position.set(...position);
    }
    if (meshRef.current) {
      meshRef.current.rotation.y = state.rotationRad;
    }
  });

  return (
    <group ref={groupRef}>
      <group rotation={[0, 0, degToRad(definition.rotation.axialTiltDeg)]}>
        <mesh
          ref={meshRef}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(definition.id);
          }}
          scale={selected ? 1.05 : 1}
        >
          <sphereGeometry args={[radius, segments, segments]} />
          <meshStandardMaterial
            color={definition.color ?? "#aaaaaa"}
            roughness={0.75}
            metalness={0.05}
            emissive={selected ? definition.color ?? "#ffffff" : "#000000"}
            emissiveIntensity={selected ? 0.28 : 0}
          />
        </mesh>
        {definition.rings && definition.rings.length > 0 && (
          <PlanetaryRings
            rings={definition.rings}
            scale={scale}
            axialTiltDeg={0}
          />
        )}
        <AxialTiltIndicator
          axialTiltDeg={0}
          length={radius * 2.2}
          visible={showAxes && selected}
        />
      </group>
      {showNameLabel && <BodyLabel name={definition.name} sphereRadius={radius} />}
      <VelocityVector
        positionScene={[0, 0, 0]}
        velocityKmS={velocityRef.current}
        scale={scale}
        visible={showVelocityVectors && selected}
      />
    </group>
  );
}
